// ============================================
// Wrapper generation — engine-owned compute entry points
// ============================================
//
// Appends the generated `[shader("compute")]` entry points AFTER the user source, so user lines
// 1..N are untouched (diagnostics stay exact) and the worker compile protocol is unchanged (one
// source, entry points auto-discovered). Slang allows out-of-order global declarations, so the
// wrappers can call user symbols declared above.
//
// Generated symbols use the `rp_` prefix — WGSL reserves identifiers starting with `__` (naga
// rejects them) and Slang passes entry-point names through verbatim.
//
//   [shader("compute")] [numthreads(64, 1, 1)]
//   void rp_kernel_doubleIt(uint3 rp_gid : SV_DispatchThreadID) { doubleIt(rp_gid.x); }
//
//   Advect rp_kinst_Advect;   // struct model: global shader param → fields legalize to bindings
//   [shader("compute")] [numthreads(64, 1, 1)]
//   void rp_kernel_Advect(uint3 rp_gid : SV_DispatchThreadID) { rp_kinst_Advect.execute(rp_gid); }

import type { ScannedKernel, KernelScanResult } from './kernelScan';

/** Generated-symbol prefixes (shared with reflectCompute, which strips them back to user names). */
export const KERNEL_ENTRY_PREFIX = 'rp_kernel_';
export const KERNEL_INSTANCE_PREFIX = 'rp_kinst_';

export interface PreparedComputeModule {
  /** User source + generated wrapper block (compile THIS). */
  wrappedSource: string;
  /** Lines 1..userLineCount are user source; anything above is generated. */
  userLineCount: number;
  kernels: ScannedKernel[];
  /** Wrapper line ranges per kernel (in wrappedSource coordinates), for diagnostics remap. */
  wrapperRanges: { kernel: string; startLine: number; endLine: number }[];
}

/** The WGSL/dispatch entry-point name a kernel compiles to. */
export function entryPointOf(kernelName: string): string {
  return `${KERNEL_ENTRY_PREFIX}${kernelName}`;
}

/** The generated global-instance name of a struct kernel. */
export function instanceNameOf(kernelName: string): string {
  return `${KERNEL_INSTANCE_PREFIX}${kernelName}`;
}

function dispatchIdExpr(idType: ScannedKernel['idType']): string {
  switch (idType) {
    case 'uint':
      return 'rp_gid.x';
    case 'uint2':
      return 'rp_gid.xy';
    case 'uint3':
      return 'rp_gid';
  }
}

/**
 * Build the compilable module: user source + appended entry wrappers for every scanned kernel.
 * Pure text assembly — call after `scanKernels` reported no errors (kernels may still be empty,
 * in which case wrappedSource === source).
 */
export function prepareComputeModule(source: string, scan: KernelScanResult): PreparedComputeModule {
  const userLineCount = source.split('\n').length;
  const lines: string[] = [];
  const wrapperRanges: PreparedComputeModule['wrapperRanges'] = [];

  // Line accounting: wrapper block starts on userLineCount + 1 (the '\n' we join with).
  let cursor = userLineCount + 1;
  const push = (line: string): void => {
    lines.push(line);
    cursor++;
  };

  push('// ==== engine-generated compute entry points (redpew; do not author below this line) ====');
  for (const k of scan.kernels) {
    const start = cursor;
    const [x, y, z] = k.numThreads;
    if (k.model === 'struct') {
      push(`${k.name} ${instanceNameOf(k.name)};`);
      push(`[shader("compute")] [numthreads(${x}, ${y}, ${z})]`);
      push(`void ${entryPointOf(k.name)}(uint3 rp_gid : SV_DispatchThreadID) { ${instanceNameOf(k.name)}.execute(rp_gid); }`);
    } else {
      push(`[shader("compute")] [numthreads(${x}, ${y}, ${z})]`);
      push(`void ${entryPointOf(k.name)}(uint3 rp_gid : SV_DispatchThreadID) { ${k.name}(${dispatchIdExpr(k.idType)}); }`);
    }
    wrapperRanges.push({ kernel: k.name, startLine: start, endLine: cursor - 1 });
  }

  const wrappedSource = scan.kernels.length ? `${source}\n${lines.join('\n')}\n` : source;
  return { wrappedSource, userLineCount, kernels: scan.kernels, wrapperRanges };
}

/**
 * Remap a diagnostic line from wrappedSource coordinates to user-source coordinates. Lines inside a
 * kernel's wrapper block map to that kernel's declaration line with a message prefix; user lines
 * pass through unchanged.
 */
export function remapDiagnostic(
  prepared: PreparedComputeModule,
  line: number,
  message: string,
): { line: number; message: string } {
  if (line <= prepared.userLineCount) return { line, message };
  const range = prepared.wrapperRanges.find((r) => line >= r.startLine && line <= r.endLine);
  if (!range) return { line: 0, message: `[generated] ${message}` };
  const kernel = prepared.kernels.find((k) => k.name === range.kernel);
  return {
    line: kernel?.declLine ?? 0,
    message: `[kernel '${range.kernel}' generated entry] ${message}`,
  };
}
